// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { ProviderRoleCode } from '@polarissleep/scheduling';

/** Re-export for use across the app, extended with clinical-ops-rcm */
export type Role = ProviderRoleCode | 'clinical-ops-rcm';

export const ROLES = {
  PHYSICIAN: 'physician',
  NURSE_PRACTITIONER: 'nurse-practitioner',
  CARE_COORDINATOR: 'care-coordinator',
  SOFTWARE_ENGINEER: 'software-engineer',
  CLINICAL_OPS_RCM: 'clinical-ops-rcm',
} as const satisfies Record<string, Role>;

const PERMISSIONS = {
  'review-triage': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'evaluate-triage': [ROLES.CARE_COORDINATOR, ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER],
  'complete-intake-form': [ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'view-intake-cc-status': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'place-hsat-order': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'admin-panel': [ROLES.PHYSICIAN, ROLES.CARE_COORDINATOR],
  'review-hsat-results': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'interpret-hsat': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'chat-with-patient': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'schedule-appointment': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'conduct-appointment': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'contact-patient': [ROLES.CARE_COORDINATOR],
  'prescribe-cpap': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'prescribe-medications': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER],
  'adjust-treatment': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'interpret-dme-data': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'change-patient-status': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'edit-patient-profile': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'manage-patient-treatments': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'manage-encounter-treatments': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER],
  // Referral orders are MedicationRequests. The care-coordinator AccessPolicy grants write only to MedicationRequests
  // carrying the referral-type coding; every other MedicationRequest stays read-only for care coordinators.
  'create-referral-order': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'manage-patient-payments': [ROLES.CARE_COORDINATOR],
  'view-tasks': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'view-patient-list': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'view-clinical-chart': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'view-staffing': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR, ROLES.CLINICAL_OPS_RCM],
  'manage-billing': [ROLES.PHYSICIAN, ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR, ROLES.CLINICAL_OPS_RCM],
  'refer-out-patient': [ROLES.NURSE_PRACTITIONER, ROLES.CARE_COORDINATOR],
  'manage-provider-availability': [ROLES.CARE_COORDINATOR],
  'author-coordinator-note': [ROLES.CARE_COORDINATOR],
  'run-zoom-integration-test': [],
  'manage-staff': [],
  'manage-notification-templates': [],
} as const satisfies Record<string, readonly Role[]>;

export type Permission = keyof typeof PERMISSIONS;

export interface HasPermissionOptions {
  role: Role | undefined;
  permission: Permission;
  isAdmin?: boolean;
}

export interface ClinicViewAccessOptions {
  role: Role | undefined;
  isAdmin?: boolean;
}

export function hasPermission({ role, permission, isAdmin }: HasPermissionOptions): boolean {
  if (isAdmin) {
    return true;
  }
  if (!role) {
    return false;
  }
  return (PERMISSIONS[permission] as readonly string[]).includes(role);
}

export function canViewClinicAppointments({ role, isAdmin }: ClinicViewAccessOptions): boolean {
  if (isAdmin) {
    return true;
  }

  return role === ROLES.CARE_COORDINATOR || role === ROLES.PHYSICIAN || role === ROLES.NURSE_PRACTITIONER;
}

export function countPermissionsForRole(role: Role): number {
  let count = 0;
  for (const allowedRoles of Object.values(PERMISSIONS)) {
    if ((allowedRoles as readonly string[]).includes(role)) {
      count += 1;
    }
  }
  return count;
}
